import I18n from 'I18n';
import { Select } from 'foundations-components/transitional/components/Select';
import { isCurrencyCodeString, resolveCurrencyCode } from 'pricing-pages-api/currency/types';
import { useEffect } from 'react';
import { useGetCurrencyStore, useSetCurrencyStore } from '../../context/CurrencyContext';
import { useGlobalLoadingState } from '../../context/GlobalLoadingStateContext';
import { useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { trackCurrencyPickerInteraction } from '../../v2/utils/tracking/trackingEventUtils';
import { useQueryParamCurrency } from '../../v2/utils/urls/appQueryParamUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const CurrencyPicker = () => {
  const setCurrencyStore = useSetCurrencyStore();
  const {
    availableCurrencyCodes,
    lockedCurrencyCode,
    displayedCurrencyCode
  } = useGetCurrencyStore(store => store);
  const isCurrencyDataLoading = useGlobalLoadingState('Currencies');
  const currencyCode = useDisplayedCurrencyCode();
  const {
    queryParamCurrency,
    setQueryParamCurrency
  } = useQueryParamCurrency();

  // Initialize Redux store from URL query param when component mounts or URL changes
  useEffect(() => {
    // Only initialize if currency data is loaded and there's a currency in the URL
    if (isCurrencyDataLoading || !queryParamCurrency) {
      return;
    }

    // Only update if URL currency differs from Redux store currency
    if (queryParamCurrency !== displayedCurrencyCode) {
      const validatedCurrencyCode = resolveCurrencyCode({
        availableCurrencyCodes,
        lockedCurrencyCode,
        displayedCurrencyCode,
        currencyCode: queryParamCurrency
      });
      if (isCurrencyCodeString(validatedCurrencyCode) && validatedCurrencyCode !== displayedCurrencyCode) {
        const showCurrencyCodeUnit = validatedCurrencyCode === 'USD';
        setCurrencyStore({
          displayedCurrencyCode: validatedCurrencyCode,
          showCurrencyCodeUnit
        });
      }
    }
  }, [queryParamCurrency, displayedCurrencyCode, isCurrencyDataLoading, availableCurrencyCodes, lockedCurrencyCode, setCurrencyStore]);
  const handleChange = ({
    target: {
      value: selectedCurrencyCode
    }
  }) => {
    const isValidCurrencyCode = isCurrencyCodeString(selectedCurrencyCode);
    if (!isValidCurrencyCode) {
      return;
    }
    const validatedCurrencyCode = resolveCurrencyCode({
      availableCurrencyCodes,
      lockedCurrencyCode,
      displayedCurrencyCode,
      currencyCode: selectedCurrencyCode
    });
    const showCurrencyCodeUnit = validatedCurrencyCode === 'USD';
    setCurrencyStore({
      displayedCurrencyCode: validatedCurrencyCode,
      showCurrencyCodeUnit
    });
    setQueryParamCurrency(validatedCurrencyCode);
    if (validatedCurrencyCode) {
      trackCurrencyPickerInteraction({
        action: 'select currency',
        currency: validatedCurrencyCode
      });
    }
  };
  const currencyOptions = availableCurrencyCodes.map(availableCurrency => ({
    text: `${availableCurrency} (${I18n.currencySymbols[availableCurrency].symbol})`,
    value: availableCurrency
  }));
  const shouldShowSelector = availableCurrencyCodes && availableCurrencyCodes.length > 1;

  // If the preferred currency has not loaded yet, we don't want to show the selector with the default currency yet
  if (!shouldShowSelector) {
    return /*#__PURE__*/_jsx("span", {
      style: {
        minHeight: '40px'
      }
    });
  }
  return /*#__PURE__*/_jsx(Select, {
    buttonUse: "transparent",
    menuWidth: 200,
    menuHeight: 320,
    options: currencyOptions,
    onChange: handleChange,
    defaultValue: currencyCode,
    value: currencyCode,
    "data-selenium-id": "currency-picker"
  });
};
export default CurrencyPicker;